import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ScrollArea,
  Box,
  Button,
  Select,
  Divider,
  Flex,
  ActionIcon,
  Group,
  Text,
  Collapse,
} from "@mantine/core";
import {
  IconArchive,
  IconCalendar,
  IconDownload,
  IconChevronDown,
  IconChevronUp,
  IconFileText,
} from "@tabler/icons-react";
import { useProfileStore } from "../../store/profileStore";
import { useStyles } from "../../styles/TaxInfoSidebar.styles";
import { DocumentGroupItem } from "../Sidebar/DocumentPopover/DocumentGroupItem.tsx";
import { DocumentActionsModal } from "../Sidebar/DocumentPopover/DocumentActionsModal.tsx";
import type { Section, SelectedDoc, DocumentItem } from "../../types";
import NavigationItem from "../Sidebar/NavigationItem";
import usePDFGenerator from "../../hooks/usePDFGenerator";
import DocumentUpload from "../Sidebar/DocumentUpload";
import { FORM2ID } from "@/constants.ts";

// Create reverse mapping from backend FormType to frontend form labels
const ID2FORM: Record<string, string> = Object.entries(FORM2ID).reduce(
  (acc, [formLabel, formId]) => {
    acc[formId] = formLabel;
    return acc;
  },
  {} as Record<string, string>
);

interface TaxInfoSidebarProps {
  onNavigate: (sectionIndex: number) => void;
  sections: Section[];
  activeSection?: number;
  onDocumentSelect: (selectedDoc: SelectedDoc) => void;
}

export const TaxInfoSidebar: React.FC<TaxInfoSidebarProps> = ({
  onNavigate,
  sections,
  activeSection = 0,
  onDocumentSelect,
}) => {
  const { classes, cx } = useStyles();
  const [expandedFormTypes, setExpandedFormTypes] = useState<
    Record<string, boolean>
  >({});
  const [isMobile, setIsMobile] = useState(false);

  const {
    documents,
    headerData,
    contentData,
    setIsSplitView,
    setActiveDocument,
    hasArchivedDocuments,
    showArchivedDocuments,
    setShowArchivedDocuments,
  } = useProfileStore();

  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string; taxYear: string }>();

  const activeYear = useProfileStore((state) => state.activeYear);
  const setActiveYear = useProfileStore((state) => state.setActiveYear);
  const availableYears = useProfileStore((state) => state.availableYears);

  // Group documents by form type
  const formTypeDocuments = useMemo(() => {
    const grouped: Record<string, DocumentItem[]> = {};

    // Group documents by their documentType
    for (const document of documents) {
      const formType = document.documentType || "other";
      if (!grouped[formType]) {
        grouped[formType] = [];
      }
      grouped[formType].push(document);
    }

    // Sort form types alphabetically by their display names
    const sortedEntries = Object.entries(grouped).sort(([typeA], [typeB]) => {
      const labelA = ID2FORM[typeA] || typeA;
      const labelB = ID2FORM[typeB] || typeB;
      return labelA.localeCompare(labelB);
    });

    return Object.fromEntries(sortedEntries);
  }, [documents]);

  useEffect(() => {
    const checkMobile = (): void => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Set initial value
    checkMobile();

    // Add event listener for window resize
    window.addEventListener("resize", checkMobile);

    // Cleanup
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Get the generatePDF function from our custom hook
  const generatePDF = usePDFGenerator();

  const handleDownloadPDF = (): void => {
    generatePDF(headerData, contentData);
  };

  const toggleFormType = (formType: string): void => {
    setExpandedFormTypes((prev) => ({
      ...prev,
      [formType]: !prev[formType],
    }));
  };

  return (
    <Box
      data-tooltip-id="client-sidebar"
      className={cx(classes.sidebarContainer, {
        [classes.stickySidebar]: !isMobile,
      })}
    >
      <ScrollArea className={classes.scrollArea}>
        <Flex
          align="center"
          justify="space-between"
          className={classes.yearSelect}
        >
          <Select
            placeholder="Select year"
            data={availableYears.map((year) => ({
              value: String(year),
              label: String(year),
            }))}
            value={activeYear ? String(activeYear) : null}
            onChange={(value) => {
              if (value && userId) {
                const numericValue = parseInt(value, 10);
                // Navigate to new URL with updated year
                navigate(`/clients/${userId}/${numericValue}`);
                // Update store (will be synced from URL in ClientProfile)
                setActiveYear(numericValue);
              }
            }}
            leftSection={<IconCalendar size={16} />}
            label="Select Tax Year"
            className={classes.yearSelect}
          />
          <ActionIcon
            onClick={handleDownloadPDF}
            className={"download-client-data"}
            variant="subtle"
            size={"xl"}
          >
            <IconDownload size={25} />
          </ActionIcon>
        </Flex>
        <Divider className={classes.divider} />
        {sections.map((section, index) => (
          <NavigationItem
            key={section.key}
            section={section}
            isActive={index === activeSection}
            onClick={() => {
              setActiveDocument(null);
              onDocumentSelect({
                presignedUrl: null,
                label: null,
                key: null,
                userType: null,
              });
              onNavigate(index);
              setIsSplitView(false);
            }}
          />
        ))}
        <Divider className={classes.divider} />

        {/* Document Form Types */}
        {Object.entries(formTypeDocuments).map(([formType, docs]) => {
          const formLabel = ID2FORM[formType] || formType;
          const isExpanded = expandedFormTypes[formType] ?? true; // Default to expanded

          return (
            <Box key={formType} className={classes.documentItem}>
              <Group
                className={classes.documentHeader}
                onClick={() => toggleFormType(formType)}
                wrap="nowrap"
                role="button"
                tabIndex={0}
                aria-expanded={isExpanded}
                onKeyDown={(e) => e.key === "Enter" && toggleFormType(formType)}
              >
                <IconFileText size={16} style={{ flexShrink: 0 }} />
                <Text size="sm" fw={500} className={classes.documentText}>
                  {formLabel}
                </Text>
                {isExpanded ? (
                  <IconChevronUp size={16} className={classes.chevron} />
                ) : (
                  <IconChevronDown size={16} className={classes.chevron} />
                )}
              </Group>

              <Collapse in={isExpanded}>
                <Box className={classes.documentContent}>
                  {docs.map((item) => (
                    <DocumentGroupItem
                      key={item.key}
                      item={item}
                      onSelect={(url) =>
                        onDocumentSelect({
                          label: item.label,
                          userType: item.userType,
                          key: item.key,
                          presignedUrl: url,
                          docId: item.docId,
                        })
                      }
                    />
                  ))}
                </Box>
              </Collapse>
            </Box>
          );
        })}

        {hasArchivedDocuments && (
          <Box px={8} pb={8}>
            <Button
              fullWidth
              size="compact-sm"
              variant={showArchivedDocuments ? "filled" : "light"}
              color="orange"
              leftSection={<IconArchive size={14} />}
              onClick={() => setShowArchivedDocuments(!showArchivedDocuments)}
            >
              {showArchivedDocuments ? "Hide Archived" : "Show Archived"}
            </Button>
          </Box>
        )}
        <DocumentUpload />
        <Flex justify="center" align="center" mb={10}></Flex>
      </ScrollArea>

      {/* Render modal once at the sidebar level */}
      <DocumentActionsModal />
    </Box>
  );
};
